import { getActiveTrace } from '@atlaskit/react-ufo/experience-trace-id-context';

export type CbrCookieInvestigationMetadata = {
	storageKey: string;
	valueFingerprint: string;
};

type CbrCookieInvestigationEndpoint = 'agg' | 'cgraphql';

const SAMPLE_RATE = 0.01;

let fallbackPageLoadId: string | undefined;
let currentPageLoadId: string | undefined;
let isCurrentPageLoadSampled = false;
let fingerprintsByStorageKey = new Map<string, Set<string>>();

const createFallbackPageLoadId = (): string => {
	const randomUuid = globalThis.crypto?.randomUUID?.();
	return randomUuid ?? `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
};

const getPageLoadId = (): string => {
	const traceId = getActiveTrace()?.traceId;
	if (traceId) {
		return traceId;
	}

	fallbackPageLoadId ??= createFallbackPageLoadId();
	return fallbackPageLoadId;
};

export const collectCbrCookieInvestigation = (
	metadata: CbrCookieInvestigationMetadata,
	endpoint: CbrCookieInvestigationEndpoint,
): void => {
	const pageLoadId = getPageLoadId();
	if (pageLoadId !== currentPageLoadId) {
		currentPageLoadId = pageLoadId;
		isCurrentPageLoadSampled = Math.random() < SAMPLE_RATE;
		fingerprintsByStorageKey = new Map();
	}

	if (!isCurrentPageLoadSampled) {
		return;
	}

	const previousFingerprints = fingerprintsByStorageKey.get(metadata.storageKey);
	const hasPreviousWriteForKey = Boolean(previousFingerprints);
	const isDuplicateValueForKey = previousFingerprints?.has(metadata.valueFingerprint) ?? false;

	if (previousFingerprints) {
		previousFingerprints.add(metadata.valueFingerprint);
	} else {
		fingerprintsByStorageKey.set(metadata.storageKey, new Set([metadata.valueFingerprint]));
	}

	void import(
		/* webpackChunkName: "loadable-confluenceanalytics-web-client" */ '@confluence/analytics-web-client'
	).then(({ getAnalyticsWebClient }) => {
		void getAnalyticsWebClient().then((analyticsWebClient) => {
			analyticsWebClient.sendOperationalEvent({
				source: 'ui',
				actionSubject: 'localStoragePersistenceMutation',
				action: 'requested',
				attributes: {
					eventName: 'cbr.cookie.investigation',
					storageKey: metadata.storageKey,
					endpoint,
					pageLoadId,
					hasPreviousWriteForKey,
					isDuplicateValueForKey,
					sampleRate: SAMPLE_RATE,
				},
			});
		});
	});
};

export const resetCbrCookieInvestigationForTests = (): void => {
	fallbackPageLoadId = undefined;
	currentPageLoadId = undefined;
	isCurrentPageLoadSampled = false;
	fingerprintsByStorageKey = new Map();
};
